import { Badge, Box, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ArrowBack, MailOutlined } from '@mui/icons-material';
import { useIsMobile, useStore } from '@uplift/hooks';
import { AsyncButton, Button, ContextualNavigationLayout, useMessagingContext } from '@uplift/ui-v2';

export type OnboardingLayoutProps = {
  onBack: () => void;
  hasBackButton?: boolean;
  fullScreen?: boolean;
  onExit: () => void;
  isOnboardingCompleted?: boolean;
};

const MessagingButton = () => {
  const messagingContext = useMessagingContext();

  return (
    <IconButton onClick={() => messagingContext.openModal()}>
      <Badge badgeContent={messagingContext.unreadMessagesCount} color="error">
        <MailOutlined />
      </Badge>
    </IconButton>
  );
};

export const OnboardingLayout: React.FC<OnboardingLayoutProps> = props => {
  const { authStore } = useStore();
  const isMobile = useIsMobile();
  const { classes, cx } = useOnboardingLayoutStyles();
  const exitButtonLabel = authStore.isLoggedIn && !props.isOnboardingCompleted ? 'Log Out' : 'Exit';

  return (
    <div className={cx(classes.root, !props.fullScreen && classes.rootPadding)}>
      <ContextualNavigationLayout
        classes={{
          root: !props.fullScreen && classes.contextualNavigationRoot,
        }}
        headerProps={{
          leftButton: props.hasBackButton && (isMobile || props.fullScreen) && (
            <Button
              type="button"
              startIcon={<ArrowBack />}
              onClick={props.onBack}
              data-testid="back-btn"
            >
              Back
            </Button>
          ),
          rightButton: (
            <>
              {authStore.isLoggedIn && <MessagingButton />}
              <AsyncButton onClick={props.onExit} data-testid="exit-logout-btn">
                {exitButtonLabel}
              </AsyncButton>
            </>
          ),
        }}
        fullWidth
      >
        <Box display="flex" flexDirection="column" flex={1}>
          {props.children}
        </Box>
      </ContextualNavigationLayout>
    </div>
  );
};

const useOnboardingLayoutStyles = makeStyles({ name: 'OnboardingLayout' })(theme => ({
  root: {
    height: '100%',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.beige.main,
    }
  },
  rootPadding: {
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 4)
    }
  },
  contextualNavigationRoot: {
    [theme.breakpoints.up('md')]: {
      flex: 1,
      height: 'unset',
      background: theme.palette.background.paper,
      maxHeight: '86.5vh',
      width: '84.66vw',
      maxWidth: '1280px',
      margin: 'auto',
      top: '-3.6vh',
      boxShadow: theme.shadows[2],
      borderRadius: theme.shape.borderRadius,
      overflow: 'hidden'
    }
  }
}));
